import type { DashboardInstance } from "../../../pages/dashboard/mock";
import styles from "./SwitcherTabStrip.module.css";

type SwitcherTabStripProps = {
  instances: ReadonlyArray<DashboardInstance>;
  currentId: string;
  onSelect: (id: string) => void;
  onLaunchNew?: () => void;
  canLaunch?: boolean;
  launchTooltip?: string;
  // Rendered in place of the launch button for zero-budget collaborators:
  // they have no entitlement to launch, so we link them to purchase instead.
  purchaseCta?: { label: string; href: string };
};

const PlusIcon = (): JSX.Element => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M7 3v8M3 7h8" />
  </svg>
);

export const SwitcherTabStrip = ({
  instances,
  currentId,
  onSelect,
  onLaunchNew,
  canLaunch = true,
  launchTooltip,
  purchaseCta,
}: SwitcherTabStripProps): JSX.Element => (
  <div className={styles.strip} role="tablist" aria-label="Your instances">
    {instances.map((inst) => {
      const selected = inst.id === currentId;
      const isLocked = inst.locked === true;
      const dotKind = isLocked ? "locked" : inst.status;
      return (
        <button
          key={inst.id}
          type="button"
          role="tab"
          aria-selected={selected}
          className={`${styles.tab} ${selected ? styles.tabSelected : ""}`}
          onClick={() => onSelect(inst.id)}
          title={isLocked ? "We're setting this up for you" : undefined}
        >
          <span
            className={`${styles.statusDot} ${styles[dotKind] ?? ""}`}
            aria-hidden
          />
          <span className={styles.name}>{inst.name}</span>
          {isLocked ? <span className={styles.lockedHint}>· in setup</span> : null}
        </button>
      );
    })}
    {purchaseCta !== undefined ? (
      <a
        className={styles.launchTab}
        href={purchaseCta.href}
        target="_blank"
        rel="noreferrer"
        aria-label={purchaseCta.label}
      >
        <PlusIcon />
        <span>{purchaseCta.label}</span>
      </a>
    ) : onLaunchNew !== undefined ? (
      <button
        type="button"
        className={styles.launchTab}
        onClick={() => onLaunchNew()}
        aria-label="Launch new instance"
        disabled={!canLaunch}
        title={!canLaunch ? launchTooltip : undefined}
      >
        <PlusIcon />
        <span>Launch new</span>
      </button>
    ) : null}
  </div>
);
